import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, BookOpen, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import { useTheme } from '../context/ThemeContext';
import Header from './Header';

const CourseCalendar = ({ journalEntries, completedDays, onBack, onSelectDay }) => {
  const { isDark } = useTheme();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedEntry, setSelectedEntry] = useState(null);

  // Get days in month
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    return { daysInMonth, startingDay, year, month };
  };

  const { daysInMonth, startingDay, year, month } = getDaysInMonth(currentMonth);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Get entries for current month
  const getEntriesForDate = (day) => {
    const dateStr = new Date(year, month, day).toDateString();
    return Object.values(journalEntries).filter(entry => {
      const entryDate = new Date(entry.date).toDateString();
      return entryDate === dateStr;
    });
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(year, month + 1, 1));
  };

  const today = new Date();
  const isToday = (day) => {
    return day === today.getDate() && 
           month === today.getMonth() && 
           year === today.getFullYear();
  };

  // Generate calendar grid
  const calendarDays = [];
  for (let i = 0; i < startingDay; i++) {
    calendarDays.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    calendarDays.push(i);
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-stone-950' : 'bg-stone-50'}`}>
      <Header />
      
      <div className="pt-28 pb-20 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <Button
                onClick={onBack}
                variant="ghost"
                className={isDark ? 'text-stone-400 hover:text-stone-200' : 'text-stone-600 hover:text-stone-800'}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back to Course
              </Button>
              <h1 className={`text-3xl font-bold mt-2 ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
                Journal Calendar
              </h1>
              <p className={`mt-1 ${isDark ? 'text-stone-400' : 'text-stone-600'}`}>
                View your daily reflections and progress
              </p>
            </div>
            
            {/* Progress summary */}
            <div className={`text-right px-6 py-4 rounded-2xl ${isDark ? 'bg-stone-900' : 'bg-white shadow-lg'}`}>
              <p className={`text-sm ${isDark ? 'text-stone-400' : 'text-stone-600'}`}>Course Progress</p>
              <p className={`text-3xl font-bold ${isDark ? 'text-amber-500' : 'text-amber-600'}`}>
                {completedDays.length}/7
              </p>
              <p className={`text-sm ${isDark ? 'text-stone-500' : 'text-stone-500'}`}>days completed</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Calendar */}
            <div className={`lg:col-span-2 rounded-3xl border p-6 ${isDark ? 'bg-stone-900/50 border-stone-800' : 'bg-white border-stone-200 shadow-lg'}`}>
              {/* Month navigation */}
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={prevMonth}
                  className={`p-2 rounded-xl transition-colors ${isDark ? 'hover:bg-stone-800 text-stone-400' : 'hover:bg-stone-100 text-stone-600'}`}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h2 className={`text-xl font-bold ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
                  {monthNames[month]} {year}
                </h2>
                <button
                  onClick={nextMonth}
                  className={`p-2 rounded-xl transition-colors ${isDark ? 'hover:bg-stone-800 text-stone-400' : 'hover:bg-stone-100 text-stone-600'}`}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {/* Day headers */}
              <div className="grid grid-cols-7 gap-2 mb-2">
                {dayNames.map(day => (
                  <div 
                    key={day}
                    className={`text-center text-sm font-medium py-2 ${isDark ? 'text-stone-500' : 'text-stone-500'}`}
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-2">
                {calendarDays.map((day, index) => {
                  if (day === null) {
                    return <div key={`empty-${index}`} className="aspect-square" />;
                  }
                  
                  const entries = getEntriesForDate(day);
                  const hasEntry = entries.length > 0;
                  const todayClass = isToday(day);
                  
                  return (
                    <button
                      key={day}
                      onClick={() => hasEntry && setSelectedEntry(entries[0])}
                      className={`aspect-square rounded-xl flex flex-col items-center justify-center transition-all ${
                        hasEntry
                          ? isDark
                            ? 'bg-amber-500/20 border-2 border-amber-500/40 hover:bg-amber-500/30 cursor-pointer'
                            : 'bg-amber-100 border-2 border-amber-300 hover:bg-amber-200 cursor-pointer'
                          : todayClass
                          ? isDark
                            ? 'bg-stone-800 border-2 border-amber-500'
                            : 'bg-amber-50 border-2 border-amber-400'
                          : isDark
                          ? 'bg-stone-800/50 hover:bg-stone-800'
                          : 'bg-stone-50 hover:bg-stone-100'
                      }`}
                    >
                      <span className={`text-sm font-medium ${
                        hasEntry 
                          ? isDark ? 'text-amber-400' : 'text-amber-700'
                          : todayClass
                          ? 'text-amber-500'
                          : isDark ? 'text-stone-400' : 'text-stone-600'
                      }`}>
                        {day}
                      </span>
                      {hasEntry && (
                        <BookOpen className={`w-3 h-3 mt-1 ${isDark ? 'text-amber-500' : 'text-amber-600'}`} />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex items-center gap-6 mt-6 pt-4 border-t border-stone-800">
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded ${isDark ? 'bg-amber-500/20 border border-amber-500/40' : 'bg-amber-100 border border-amber-300'}`} />
                  <span className={`text-sm ${isDark ? 'text-stone-400' : 'text-stone-600'}`}>Journal entry</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded border-2 ${isDark ? 'border-amber-500 bg-stone-800' : 'border-amber-400 bg-amber-50'}`} />
                  <span className={`text-sm ${isDark ? 'text-stone-400' : 'text-stone-600'}`}>Today</span>
                </div>
              </div>
            </div>

            {/* Entry preview / Day selector */}
            <div className={`rounded-3xl border p-6 ${isDark ? 'bg-stone-900/50 border-stone-800' : 'bg-white border-stone-200 shadow-lg'}`}>
              {selectedEntry ? (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className={`font-semibold ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
                      Day {selectedEntry.day} Journal
                    </h3>
                    <button
                      onClick={() => setSelectedEntry(null)}
                      className={isDark ? 'text-stone-500 hover:text-stone-300' : 'text-stone-400 hover:text-stone-600'}
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <p className={`text-sm mb-4 ${isDark ? 'text-stone-500' : 'text-stone-500'}`}>
                    {new Date(selectedEntry.date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                  <div className={`p-4 rounded-xl mb-4 max-h-60 overflow-y-auto ${isDark ? 'bg-stone-800' : 'bg-stone-50'}`}>
                    <p className={`whitespace-pre-wrap ${isDark ? 'text-stone-300' : 'text-stone-700'}`}>
                      {selectedEntry.content}
                    </p>
                  </div>
                  <Button
                    onClick={() => onSelectDay(selectedEntry.day)}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-stone-950"
                  >
                    Edit Day {selectedEntry.day}
                  </Button>
                </>
              ) : (
                <>
                  <h3 className={`font-semibold mb-4 ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
                    Course Days
                  </h3>
                  <div className="space-y-2">
                    {[1, 2, 3, 4, 5, 6, 7].map(day => {
                      const isCompleted = completedDays.includes(day);
                      return (
                        <button
                          key={day}
                          onClick={() => onSelectDay(day)}
                          className={`w-full flex items-center justify-between p-3 rounded-xl transition-colors ${
                            isDark 
                              ? 'bg-stone-800 hover:bg-stone-700' 
                              : 'bg-stone-50 hover:bg-stone-100'
                          }`}
                        >
                          <span className={isDark ? 'text-stone-300' : 'text-stone-700'}>
                            Day {day}
                          </span>
                          {isCompleted ? (
                            <Check className="w-5 h-5 text-green-500" />
                          ) : (
                            <ChevronRight className={`w-5 h-5 ${isDark ? 'text-stone-500' : 'text-stone-400'}`} />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseCalendar;
